// Calculate distance between two GPS points using the Haversine formula
const calculateDistance = (point1, point2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (point2.lat - point1.lat) * Math.PI / 180;
    const dLon = (point2.lon - point1.lon) * Math.PI / 180;

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in meters
};

// Format time in MM:SS or HH:MM:SS format
const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
};

// Calculate centroid (average position) of multiple points
const calculateCentroid = (points) => {
    if (points.length === 0) return null;
    const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
    const avgLon = points.reduce((sum, p) => sum + p.lon, 0) / points.length;
    return { lat: avgLat, lon: avgLon };
};

// Calculate median value
const calculateMedian = (values) => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Calculate percentile of values
const calculatePercentile = (values, percentile) => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[Math.max(0, index)];
};

// Detect the start/finish zone using clustering
const detectStartFinishZone = (points, searchRadius = 50) => {
    const returnPoints = [];
    
    // Find points that are close to other points (potential return zones)
    for (let i = 0; i < points.length - 1; i++) {
        for (let j = i + 1; j < points.length; j++) {
            const distance = calculateDistance(points[i], points[j]);
            if (distance < searchRadius && j - i > 10) {
                returnPoints.push({ index: j, point: points[j], distance });
            }
        }
    }
    
    if (returnPoints.length === 0) return points[0]; // Fallback to start
    
    // Cluster nearby return points and find centroid
    const clusters = [];
    const used = new Set();
    
    for (const returnPoint of returnPoints) {
        if (used.has(returnPoint.index)) continue;
        
        const cluster = [returnPoint.point];
        used.add(returnPoint.index);
        
        for (const other of returnPoints) {
            if (!used.has(other.index) && calculateDistance(returnPoint.point, other.point) < searchRadius) {
                cluster.push(other.point);
                used.add(other.index);
            }
        }
        
        if (cluster.length > 1) {
            clusters.push({ centroid: calculateCentroid(cluster), count: cluster.length });
        }
    }
    
    // Return the centroid of the largest cluster
    if (clusters.length > 0) {
        return clusters.sort((a, b) => b.count - a.count)[0].centroid;
    }
    
    return points[0];
};

// Get adaptive threshold based on track characteristics
const getAdaptiveThreshold = (points) => {
    const distances = [];
    
    // Calculate distances between consecutive points
    for (let i = 1; i < Math.min(points.length, 100); i++) {
        const dist = calculateDistance(points[i - 1], points[i]);
        if (dist > 0) distances.push(dist);
    }
    
    if (distances.length === 0) return 25; // Fallback
    
    // Use 10th percentile of point-to-point distances
    const baseDistance = calculatePercentile(distances, 10);
    return Math.max(baseDistance * 5, 15); // At least 15m, scale by 5x
};

// Detect laps by velocity changes (runners slow at finish)
const detectLapsByVelocity = (points) => {
    if (points.length < 10) return [];
    
    const velocities = [];
    const lapCandidates = [];
    
    // Calculate velocity at each point
    for (let i = 1; i < points.length; i++) {
        const dist = calculateDistance(points[i - 1], points[i]);
        const timeDiff = (points[i].time - points[i - 1].time) / 1000; // seconds
        const velocity = timeDiff > 0 ? dist / timeDiff : 0;
        velocities.push(velocity);
    }
    
    if (velocities.length < 5) return [];
    
    // Find significant velocity drops (runners slow at finish line)
    const avgVelocity = velocities.reduce((a, b) => a + b) / velocities.length;
    const velocityThreshold = avgVelocity * 0.5; // 50% drop indicates finish
    
    for (let i = 1; i < velocities.length; i++) {
        const velocityDrop = velocities[i - 1] - velocities[i];
        if (velocityDrop > avgVelocity * 0.3 && velocities[i] < velocityThreshold) {
            lapCandidates.push(i + 1);
        }
    }
    
    return lapCandidates;
};

// Filter outlier lap durations using IQR (Interquartile Range)
// iqrMultiplier: controls sensitivity to outliers
//   - 1.0: more aggressive (stricter)
//   - 1.5: default (standard)
//   - 2.0+: less aggressive (more permissive)
const filterOutlierLaps = (lapDetails, iqrMultiplier = 1.5) => {
    if (lapDetails.length <= 3) return lapDetails;
    
    const durations = lapDetails
        .filter(lap => lap.durationSeconds !== null)
        .map(lap => lap.durationSeconds);
    
    if (durations.length <= 3) return lapDetails;
    
    // Calculate quartiles
    const sorted = [...durations].sort((a, b) => a - b);
    const q1Index = Math.floor(sorted.length * 0.25);
    const q3Index = Math.floor(sorted.length * 0.75);
    const q1 = sorted[q1Index];
    const q3 = sorted[q3Index];
    const iqr = q3 - q1;
    
    // IQR-based outlier detection (configurable multiplier)
    const lowerBound = q1 - iqrMultiplier * iqr;
    const upperBound = q3 + iqrMultiplier * iqr;
    
    return lapDetails.filter(lap => {
        if (lap.durationSeconds === null) return true;
        return lap.durationSeconds >= lowerBound && lap.durationSeconds <= upperBound;
    });
};

// Merge lap detections from multiple methods
const mergeDetections = (proximityLaps, velocityLaps, proximityPoints, proximityThreshold = 30) => {
    const merged = new Set(proximityLaps);
    
    // Add velocity-detected laps that don't conflict with proximity laps
    for (const velocityIndex of velocityLaps) {
        let hasNearbyProximityLap = false;
        
        for (const proximityIndex of proximityLaps) {
            if (Math.abs(velocityIndex - proximityIndex) < proximityThreshold) {
                hasNearbyProximityLap = true;
                break;
            }
        }
        
        // Only add if it's new and not contradicted by proximity detection
        if (!hasNearbyProximityLap) {
            merged.add(velocityIndex);
        }
    }
    
    return Array.from(merged).sort((a, b) => a - b);
};

// Detect laps in the track using hybrid method (proximity + velocity)
// options: { customThreshold, iqrMultiplier }
const countLaps = (points, customThreshold = null, options = {}) => {
    if (points.length < 10) {
        return { laps: 0, lapPoints: [], lapDetails: [] };
    }

    // Extract options with defaults
    const { iqrMultiplier = 1.5 } = options;

    // Use adaptive threshold if not provided
    const thresholdDistance = customThreshold || getAdaptiveThreshold(points);
    
    // Detect start/finish zone using clustering
    const startFinishZone = detectStartFinishZone(points, thresholdDistance * 1.5);
    
    // Method 1: Proximity-based detection
    const proximityLapPoints = [];
    let lastLapPoint = 0;
    const minDistanceForNewLap = thresholdDistance * 2;
    let maxDistanceFromStart = 0;
    let hasMovedAwayFromStart = false;

    for (let i = 1; i < points.length; i++) {
        const currentPoint = points[i];
        const distanceFromStart = calculateDistance(startFinishZone, currentPoint);

        if (distanceFromStart > maxDistanceFromStart) {
            maxDistanceFromStart = distanceFromStart;
            if (maxDistanceFromStart > minDistanceForNewLap) {
                hasMovedAwayFromStart = true;
            }
        }

        if (distanceFromStart < thresholdDistance && hasMovedAwayFromStart) {
            if (i - lastLapPoint > 5) {
                proximityLapPoints.push(i);
                lastLapPoint = i;
                maxDistanceFromStart = 0;
                hasMovedAwayFromStart = false;
            }
        }
    }

    // Method 2: Velocity-based detection
    const velocityLapPoints = detectLapsByVelocity(points);
    
    // Merge detections from both methods
    const lapPoints = mergeDetections(proximityLapPoints, velocityLapPoints, points);

    // Calculate lap details
    const lapDetails = lapPoints.map((pointIndex, lapIndex) => {
        const lapTime = points[pointIndex].time;
        const prevLapTime = lapIndex > 0 ? points[lapPoints[lapIndex - 1]].time : points[0].time;

        let lapDuration = null;
        if (lapTime && prevLapTime) {
            lapDuration = (lapTime - prevLapTime) / 1000; // Duration in seconds
        }

        return {
            lapNumber: lapIndex + 1,
            pointIndex: pointIndex,
            time: lapTime || 'unknown',
            durationSeconds: lapDuration,
            formattedDuration: lapDuration ? formatTime(lapDuration) : 'unknown'
        };
    });

    // Filter outliers using IQR method
    const lapDetailsFiltered = filterOutlierLaps(lapDetails, iqrMultiplier);
    
    // Calculate statistics
    const validLapDurations = lapDetailsFiltered
        .filter(lap => lap.durationSeconds !== null)
        .map(lap => lap.durationSeconds);
    
    const averageLapTime = validLapDurations.length > 0
        ? validLapDurations.reduce((a, b) => a + b) / validLapDurations.length
        : 0;
    
    const totalTime = validLapDurations.reduce((a, b) => a + b, 0);

    return {
        laps: lapDetailsFiltered.length,
        totalTime: formatTime(totalTime),
        averageLapTime: formatTime(averageLapTime),
        lapPoints: lapPoints,
        lapDetails: lapDetailsFiltered,
        allLapDetails: lapDetails  // Store unfiltered for outlier count
    };
};

const getHistogramBucketPoint = (point, binSize) => {
    const lat = Math.floor(point.lat / binSize) * binSize + binSize / 2;
    const lon = Math.floor(point.lon / binSize) * binSize + binSize / 2;

    return { lat, lon };
};

const getPointKey = ({ lat, lon }) => (`${lat},${lon}`);

const createHistogram = (points) => {
    const bins = new Map();

    points.forEach(point => {
        const key = getPointKey(point);
        bins.set(key, (bins.get(key) || 0) + 1);
    });

    return bins;
};

const removeDuplicatePoints = (points, minDistance = 10) => {
    if (points.length < 2) return points;

    const filteredPoints = [points[0]];
    let lastPoint = points[0];

    for (let i = 1; i < points.length; i++) {
        const currentPoint = points[i];
        const distance = calculateDistance(lastPoint, currentPoint);

        if (distance >= minDistance) {
            filteredPoints.push(currentPoint);
            lastPoint = currentPoint;
        }
    }

    return filteredPoints;
};

const separateCircularPaths = (points, binSize = 0.0001) => {
    const circularPaths = [];

    const bucketPoints = points.map(point => getHistogramBucketPoint(point, binSize));
    const histogram = createHistogram(bucketPoints, binSize);

    points.forEach(point => {
        const bucketPoint = getHistogramBucketPoint(point, binSize);
        const histogramBucket = histogram.get(getPointKey(bucketPoint));

        if (histogramBucket > 5) {
            //circularPaths.push({ ...point, ...bucketPoint });
            circularPaths.push(point);
        }

    });

    return { circularPaths };
};

// Calculate advanced metrics from lap details
const calculateAdvancedMetrics = (lapDetails, allLapDetails = null) => {
    if (!lapDetails || lapDetails.length === 0) {
        return {
            fastestLap: null,
            slowestLap: null,
            medianLapTime: null,
            stdDev: null,
            beste3LapAvg: null,
            bestCapLapAvg: null,
            trendAnalysis: null,
            startTime: null,
            endTime: null,
            totalActivityDuration: null,
            outliersFiltered: 0
        };
    }

    const durations = lapDetails
        .filter(lap => lap.durationSeconds !== null)
        .map(lap => lap.durationSeconds);

    if (durations.length === 0) {
        return {
            fastestLap: null,
            slowestLap: null,
            medianLapTime: null,
            stdDev: null,
            beste3LapAvg: null,
            bestCapLapAvg: null,
            trendAnalysis: null,
            startTime: null,
            endTime: null,
            totalActivityDuration: null,
            outliersFiltered: 0
        };
    }

    // Fastest and slowest lap
    const fastestLap = Math.min(...durations);
    const slowestLap = Math.max(...durations);

    // Median lap time
    const median = calculateMedian(durations);

    // Standard deviation
    const mean = durations.reduce((a, b) => a + b) / durations.length;
    const variance = durations.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / durations.length;
    const stdDev = Math.sqrt(variance);

    // Best 3 lap average
    const sorted = [...durations].sort((a, b) => a - b);
    const besteBeste3LapAvg = sorted.slice(0, Math.min(3, sorted.length)).reduce((a, b) => a + b) / Math.min(3, sorted.length);
    
    // Best 5 lap average
    const best5LapAvg = sorted.slice(0, Math.min(5, sorted.length)).reduce((a, b) => a + b) / Math.min(5, sorted.length);

    // Trend analysis: compare first half vs second half
    const midpoint = Math.floor(durations.length / 2);
    const firstHalf = durations.slice(0, midpoint);
    const secondHalf = durations.slice(midpoint);
    const firstHalfAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length || 0;
    const secondHalfAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length || 0;
    const trendAnalysis = firstHalfAvg !== 0 ? (secondHalfAvg - firstHalfAvg) / firstHalfAvg * 100 : 0;

    // Start/end time and activity duration
    const startTime = lapDetails[0]?.time;
    const endTime = lapDetails[lapDetails.length - 1]?.time;
    let totalActivityDuration = null;
    if (startTime && endTime && typeof startTime !== 'string' && typeof endTime !== 'string') {
        totalActivityDuration = (endTime - startTime) / 1000; // in seconds
    }

    // Count filtered outliers
    const totalLaps = allLapDetails ? allLapDetails.length : lapDetails.length;
    const outliersFiltered = Math.max(0, totalLaps - lapDetails.length);

    return {
        fastestLap,
        slowestLap,
        medianLapTime: median,
        stdDev,
        best3LapAvg: besteBeste3LapAvg,
        best5LapAvg,
        trendAnalysis,
        startTime,
        endTime,
        totalActivityDuration,
        outliersFiltered
    };
};

export {
    countLaps,
    separateCircularPaths,
    removeDuplicatePoints,
    detectStartFinishZone,
    getAdaptiveThreshold,
    detectLapsByVelocity,
    filterOutlierLaps,
    calculateCentroid,
    calculateMedian,
    calculatePercentile,
    calculateAdvancedMetrics,
    formatTime
};